import React, { useState, useEffect } from 'react';
import { v1 as uuidv1, v4 as uuidv4, v5 as uuidv5, v7 as uuidv7, validate, version } from 'uuid';
import TextArea from '../components/TextArea';
import ToolLayout from '../components/ToolLayout';

const VERSION_DESCRIPTIONS = {
  v1: 'Timestamp + MAC address. Time-ordered but may expose hardware identity.',
  v4: 'Random. Most common, no embedded info, privacy-safe.',
  v5: 'SHA-1 hash of namespace + name. Deterministic - same input = same UUID.',
  v7: 'Unix timestamp + random. Sortable, modern standard for databases.'
};

const PREDEFINED_NAMESPACES = {
  DNS: '6ba7b810-9dad-11d1-80b4-00c04fd430c8',
  URL: '6ba7b811-9dad-11d1-80b4-00c04fd430c8',
  OID: '6ba7b812-9dad-11d1-80b4-00c04fd430c8',
  X500: '6ba7b814-9dad-11d1-80b4-00c04fd430c8'
};

const UUIDTool = () => {
  const [uuidVersion, setUuidVersion] = useState('v4');
  const [count, setCount] = useState(1);
  const [customCount, setCustomCount] = useState('');
  const [namespace, setNamespace] = useState('DNS');
  const [customNamespace, setCustomNamespace] = useState('');
  const [name, setName] = useState('');
  const [generatedUuids, setGeneratedUuids] = useState('');
  const [rawUuids, setRawUuids] = useState([]);
  const [inputUuid, setInputUuid] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [formatOptions, setFormatOptions] = useState({
    uppercase: false,
    noHyphens: false,
    braces: false,
    brackets: false,
    parentheses: false,
    singleQuotes: false,
    doubleQuotes: false
  });
  const [separators, setSeparators] = useState({
    comma: false,
    space: false,
    newline: true,
    semicolon: false,
    tab: false
  });

  const formatUuid = (uuid) => {
    let result = uuid;
    if (formatOptions.noHyphens) {
      result = result.replace(/-/g, '');
    }
    if (formatOptions.uppercase) {
      result = result.toUpperCase();
    }
    if (formatOptions.singleQuotes) {
      result = `'${result}'`;
    }
    if (formatOptions.doubleQuotes) {
      result = `"${result}"`;
    }
    return result;
  };

  const formatOutput = (uuids) => {
    // Build separator string from selected options
    let separator = '';
    if (separators.comma) separator += ',';
    if (separators.semicolon) separator += ';';
    if (separators.space) separator += ' ';
    if (separators.tab) separator += '\t';
    if (separators.newline) separator += '\n';

    const needsWrapper = formatOptions.braces || formatOptions.brackets || formatOptions.parentheses;
    const hasNewline = separator.includes('\n');

    if (needsWrapper) {
      const indent = '  ';
      let open = '', close = '';
      if (formatOptions.parentheses) { open = '('; close = ')'; }
      if (formatOptions.brackets) { open = '['; close = ']'; }
      if (formatOptions.braces) { open = '{'; close = '}'; }

      if (hasNewline) {
        // Multi-line format with indentation
        const innerSeparator = separator.replace(/\n/g, '\n' + indent);
        const content = uuids.join(innerSeparator);
        return open + '\n' + indent + content + '\n' + close;
      } else {
        // Single-line format
        const content = uuids.join(separator);
        return open + content + close;
      }
    }

    return uuids.join(separator);
  };

  // Generate initial UUID on mount
  useEffect(() => {
    handleGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-format when format options or separators change
  useEffect(() => {
    if (rawUuids.length > 0) {
      const formatted = rawUuids.map(formatUuid);
      setGeneratedUuids(formatOutput(formatted));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatOptions, separators]);

  // Auto-generate when version changes (skip v5)
  useEffect(() => {
    if (uuidVersion !== 'v5') {
      handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuidVersion]);

  // Auto-generate when count changes (skip v5 without name)
  useEffect(() => {
    if (uuidVersion !== 'v5' || name) {
      handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, customCount]);

  const generateSingleUuid = () => {
    switch (uuidVersion) {
      case 'v1':
        return uuidv1();
      case 'v4':
        return uuidv4();
      case 'v5': {
        const ns = namespace === 'Custom' ? customNamespace : PREDEFINED_NAMESPACES[namespace];
        if (!validate(ns)) {
          return 'Invalid namespace UUID';
        }
        if (!name) {
          return 'Name is required for v5';
        }
        return uuidv5(name, ns);
      }
      case 'v7':
        return uuidv7();
      default:
        return uuidv4();
    }
  };

  const handleGenerate = () => {
    const actualCount = customCount ? parseInt(customCount, 10) : count;
    if (isNaN(actualCount) || actualCount < 1 || actualCount > 1000) {
      setGeneratedUuids('Invalid count (must be 1-1000)');
      setRawUuids([]);
      return;
    }

    const uuids = [];
    for (let i = 0; i < actualCount; i++) {
      const uuid = generateSingleUuid();
      if (uuid.startsWith('Invalid') || uuid.startsWith('Name is')) {
        setGeneratedUuids(uuid);
        setRawUuids([]);
        return;
      }
      uuids.push(uuid);
    }
    setRawUuids(uuids);
    const formatted = uuids.map(formatUuid);
    setGeneratedUuids(formatOutput(formatted));
  };

  const extractTimestampV1 = (uuid) => {
    try {
      const parts = uuid.split('-');
      const timeLow = parts[0];
      const timeMid = parts[1];
      const timeHighAndVersion = parts[2];
      const timeHigh = timeHighAndVersion.substring(1);
      const timestampHex = timeHigh + timeMid + timeLow;
      const timestamp = parseInt(timestampHex, 16);
      // eslint-disable-next-line no-undef
      const gregorianOffset = BigInt('122192928000000000');
      // eslint-disable-next-line no-undef
      const unixTimestamp = (BigInt(timestamp) - gregorianOffset) / BigInt(10000);
      return new Date(Number(unixTimestamp));
    } catch {
      return null;
    }
  };

  const extractTimestampV7 = (uuid) => {
    try {
      const hex = uuid.replace(/-/g, '').substring(0, 12);
      const timestamp = parseInt(hex, 16);
      return new Date(timestamp);
    } catch {
      return null;
    }
  };

  const handleValidate = () => {
    const trimmedUuid = inputUuid.trim().replace(/[{}]/g, '');

    if (!trimmedUuid) {
      setValidationResult({ valid: false, message: 'Please enter a UUID to validate' });
      return;
    }

    const isValid = validate(trimmedUuid);

    if (!isValid) {
      setValidationResult({ valid: false, message: 'Invalid UUID format' });
      return;
    }

    const uuidVersion = version(trimmedUuid);
    let timestamp = null;

    if (uuidVersion === 1) {
      timestamp = extractTimestampV1(trimmedUuid);
    } else if (uuidVersion === 7) {
      timestamp = extractTimestampV7(trimmedUuid);
    }

    setValidationResult({
      valid: true,
      version: uuidVersion,
      timestamp: timestamp,
      message: `Valid UUID v${uuidVersion}`
    });
  };

  const handleClear = () => {
    setGeneratedUuids('');
    setRawUuids([]);
    setInputUuid('');
    setValidationResult(null);
    setName('');
    setCustomCount('');
  };

  const buttonClass = "px-4 py-2 rounded font-medium transition-colors";
  const primaryButtonClass = `${buttonClass} bg-blue-600 hover:bg-blue-700 text-white`;
  const secondaryButtonClass = `${buttonClass} bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600`;
  const selectedButtonClass = `${buttonClass} bg-blue-600 text-white`;
  const unselectedButtonClass = `${buttonClass} bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600`;

  return (
    <ToolLayout title="UUID Generator">
      <div className="space-y-6">
        {/* Version Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Version</label>
          <div className="flex flex-wrap gap-2">
            {['v1', 'v4', 'v5', 'v7'].map((v) => (
              <button
                key={v}
                onClick={() => setUuidVersion(v)}
                className={uuidVersion === v ? selectedButtonClass : unselectedButtonClass}
              >
                {v.toUpperCase()}
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {VERSION_DESCRIPTIONS[uuidVersion]}
          </p>
        </div>

        {/* v5 Namespace and Name */}
        {uuidVersion === 'v5' && (
          <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Namespace</label>
              <div className="flex flex-wrap gap-2">
                {[...Object.keys(PREDEFINED_NAMESPACES), 'Custom'].map((ns) => (
                  <button
                    key={ns}
                    onClick={() => setNamespace(ns)}
                    className={namespace === ns ? selectedButtonClass : unselectedButtonClass}
                  >
                    {ns}
                  </button>
                ))}
              </div>
              {namespace === 'Custom' && (
                <input
                  type="text"
                  value={customNamespace}
                  onChange={(e) => setCustomNamespace(e.target.value)}
                  placeholder="Enter custom namespace UUID"
                  className="w-full mt-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-300 rounded border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500"
                />
              )}
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter name to hash"
                className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-300 rounded border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* Count Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Count</label>
          <div className="flex flex-wrap gap-2 items-center">
            {[1, 5, 10].map((c) => (
              <button
                key={c}
                onClick={() => { setCount(c); setCustomCount(''); }}
                className={count === c && !customCount ? selectedButtonClass : unselectedButtonClass}
              >
                {c}
              </button>
            ))}
            <input
              type="number"
              value={customCount}
              onChange={(e) => setCustomCount(e.target.value)}
              placeholder="Custom"
              min="1"
              max="1000"
              className="w-24 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-300 rounded border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Format Options */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Format</label>

          {/* Checkboxes */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formatOptions.uppercase}
                onChange={(e) => setFormatOptions({ ...formatOptions, uppercase: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Uppercase</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formatOptions.noHyphens}
                onChange={(e) => setFormatOptions({ ...formatOptions, noHyphens: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">No hyphens</span>
            </label>
          </div>

          {/* Wrapper Radio Group */}
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">Wrapper:</span>
            {[
              { value: 'none', label: 'None' },
              { value: 'braces', label: 'Braces {...}' },
              { value: 'brackets', label: 'Brackets [...]' },
              { value: 'parentheses', label: 'Parentheses (...)' }
            ].map(({ value, label }) => (
              <label key={value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="wrapper"
                  checked={value === 'none' ? (!formatOptions.braces && !formatOptions.brackets && !formatOptions.parentheses) : formatOptions[value]}
                  onChange={() => setFormatOptions({ ...formatOptions, braces: value === 'braces', brackets: value === 'brackets', parentheses: value === 'parentheses' })}
                  className="w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
              </label>
            ))}
          </div>

          {/* Quotes Radio Group */}
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">Quotes:</span>
            {[
              { value: 'none', label: 'None' },
              { value: 'singleQuotes', label: "Single '...'" },
              { value: 'doubleQuotes', label: 'Double "..."' }
            ].map(({ value, label }) => (
              <label key={value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="quotes"
                  checked={value === 'none' ? (!formatOptions.singleQuotes && !formatOptions.doubleQuotes) : formatOptions[value]}
                  onChange={() => setFormatOptions({ ...formatOptions, singleQuotes: value === 'singleQuotes', doubleQuotes: value === 'doubleQuotes' })}
                  className="w-4 h-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
              </label>
            ))}
          </div>

          {/* Separator Checkboxes */}
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">Separator:</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={separators.comma}
                onChange={(e) => setSeparators({ ...separators, comma: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Comma</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={separators.space}
                onChange={(e) => setSeparators({ ...separators, space: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Space</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={separators.newline}
                onChange={(e) => setSeparators({ ...separators, newline: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Newline</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={separators.semicolon}
                onChange={(e) => setSeparators({ ...separators, semicolon: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Semicolon</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={separators.tab}
                onChange={(e) => setSeparators({ ...separators, tab: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Tab</span>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <button onClick={handleGenerate} className={primaryButtonClass}>
            Generate
          </button>
          <button onClick={handleClear} className={secondaryButtonClass}>
            Clear
          </button>
        </div>

        {/* Output */}
        <TextArea
          value={generatedUuids}
          onChange={() => {}}
          placeholder="Generated UUIDs will appear here"
          readOnly={true}
        />

        {/* Validation Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-gray-100">Validate / Parse UUID</h3>

          <div className="space-y-4">
            <input
              type="text"
              value={inputUuid}
              onChange={(e) => setInputUuid(e.target.value)}
              placeholder="Enter UUID to validate"
              className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-300 rounded border border-gray-300 dark:border-gray-700 focus:outline-none focus:border-blue-500 font-mono"
            />

            <button onClick={handleValidate} className={primaryButtonClass}>
              Validate
            </button>

            {validationResult && (
              <div className={`p-4 rounded border ${
                validationResult.valid
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              }`}>
                <p className={`font-medium ${
                  validationResult.valid
                    ? 'text-green-700 dark:text-green-400'
                    : 'text-red-700 dark:text-red-400'
                }`}>
                  {validationResult.valid ? '✓' : '✗'} {validationResult.message}
                </p>
                {validationResult.timestamp && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Timestamp:</span> {validationResult.timestamp.toISOString()}
                    <br />
                    <span className="font-medium">Local time:</span> {validationResult.timestamp.toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </ToolLayout>
  );
};

export default UUIDTool;
